import React, { useEffect, useState } from 'react';
import { CoveringEmail, LegacyCoveringEmail, StepOptions } from '../../../api/cafAutomation';
import { isProviderEmail } from '../../email-catalogue/reviewProofs';
import CatalogueSelect from '../../email-catalogue/CatalogueSelect';
import { emailOptionLink } from '../../../api/emailCatalogue';
import { listTemplates } from '../../../api/cafWorkflows';
import TemplateSourceEditor from '../../caf-workflows/TemplateSourceEditor';
import { TemplateSummary, TokenMapping } from '../../caf-workflows/workflowTypes';

const tokenKey = (token: TokenMapping): string => JSON.stringify(Object.entries(token).sort(([a], [b]) => a.localeCompare(b)));
const subjectVariables = (subject: string): string[] => Array.from(subject.matchAll(/\{\{\s*([A-Za-z][A-Za-z0-9_]*(?:\.[A-Za-z][A-Za-z0-9_]*)*)/g), (match) => match[1]);

export const ForwardStepFields: React.FC<{
  value: CoveringEmail; scope?: 'closed_claim_default'; index: number; options: StepOptions | null; disabled: boolean; onChange: (value: CoveringEmail) => void;
}> = props => <>
  {isProviderEmail(props.value) ? <div>
    <p><strong>To:</strong> the client on the matched claim.</p>
    <p><strong>Attachments:</strong> the received lender email as a PDF, plus all its original attachments.</p>
    <p>{props.scope === 'closed_claim_default' ? 'For cancelled claims only.' : 'For active and cancelled claims.'} The workflow&apos;s existing trigger determines when it runs.</p>
    <CatalogueSelect label={`Covering email for step ${props.index + 1}`} family="forward_correspondence"
      value={props.value.catalogue_ref} options={props.options?.catalogue_options} nextCursor={props.options?.catalogue_next_cursor}
      disabled={props.disabled} onChange={catalogue_ref => props.onChange({ delivery_mode: 'provider_template_v1', catalogue_ref })} />
    <a href={emailOptionLink(props.value.catalogue_ref.option_id)} target="_blank" rel="noopener noreferrer">Edit covering email settings</a>
  </div> : <><LegacyForwardStepFields {...props} value={props.value} />
    {props.options?.catalogue_options && <CatalogueSelect label="Replace imported cover with catalogue settings" family="forward_correspondence"
      options={props.options.catalogue_options} nextCursor={props.options.catalogue_next_cursor} disabled={props.disabled}
      onChange={catalogue_ref => props.onChange({ delivery_mode: 'provider_template_v1', catalogue_ref })} />}</>}
</>;

const LegacyForwardStepFields: React.FC<{
  value: LegacyCoveringEmail; index: number; options: StepOptions | null; disabled: boolean; onChange: (value: CoveringEmail) => void;
}> = ({ value, index, options, disabled, onChange }) => {
  const [templates, setTemplates] = useState<TemplateSummary[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const [adding, setAdding] = useState(false);
  const [newId, setNewId] = useState('');
  const [editingId, setEditingId] = useState('');
  const [importing, setImporting] = useState(false);
  const n = index + 1;
  const policyReady = Array.isArray(options?.forward_excluded_template_ids) && Array.isArray(options?.forward_token_choices);
  const excluded = options?.forward_excluded_template_ids || [];
  const choices = options?.forward_token_choices || [];
  const available = policyReady ? (templates || []).filter((item) => !excluded.includes(item.template_id)) : [];
  const selected = (templates || []).find((item) => item.template_id === value.template_id);
  const unavailable = Boolean(value.template_id && !available.some((item) => item.template_id === value.template_id));
  const reserved = excluded.includes(value.template_id);
  const variables = Array.from(new Set([...(selected?.tokens || []), ...subjectVariables(value.subject), ...Object.keys(value.tokens)])).sort();

  useEffect(() => {
    let alive = true;
    setFailed(false);
    setTemplates(null);
    listTemplates().then((items) => { if (alive) setTemplates(items); }, () => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [reload]);

  const mapVariable = (variable: string, choice: string) => {
    const tokens = { ...value.tokens };
    if (choice === '') delete tokens[variable];
    else if (choices[Number(choice)]) tokens[variable] = { ...choices[Number(choice)].token };
    onChange({ ...value, tokens });
  };
  const canSetUp = policyReady && /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,199}$/.test(newId.trim()) && !excluded.includes(newId.trim());

  return (
    <div className="cur-forward-fields">
      <div className="cur-forward-summary">
        <p><strong>To:</strong> the client on the matched claim.</p>
        <p><strong>Attachments:</strong> the received lender email as a PDF, plus all its original attachments.</p>
        <p className="cur-hint">For active and cancelled claims. The workflow&apos;s existing trigger determines when it runs.</p>
      </div>
      <label className="cur-field">
        <span>Covering email</span>
        <select className="cur-input" aria-label={`Covering email for step ${n}`} value={value.template_id}
          disabled={disabled || importing || !policyReady || !templates}
          onChange={(event) => onChange({ ...value, template_id: event.target.value, tokens: {} })}>
          <option value="">Choose a covering email</option>
          {unavailable && <option value={value.template_id} disabled>{value.template_id} · {reserved ? 'reserved for another process' : 'currently unavailable'}</option>}
          {available.map((item) => <option key={item.template_id} value={item.template_id}>{item.template_id} · version {item.version}</option>)}
        </select>
      </label>
      {!policyReady && <p className="cur-problem">Covering email settings are unavailable. Reopen the workflow to try again.</p>}
      {!templates && !failed && <p className="cur-hint">Loading covering emails…</p>}
      {failed && <p className="cur-problem" role="alert">Couldn&apos;t load covering emails. Your selected email and settings are kept.</p>}
      {templates && policyReady && available.length === 0 && <p className="cur-hint">No covering emails are available. Add a covering email below, then choose and preview it.</p>}
      {reserved && <p className="cur-problem">This email is reserved for another process. Choose a covering email suitable for active and cancelled claims.</p>}
      {templates && unavailable && !reserved && <p className="cur-problem">The selected covering email is unavailable. Add its source or choose another email before publishing.</p>}
      <div className="cur-inline">
        <button type="button" className="cur-linkish" disabled={disabled || importing} onClick={() => setReload((current) => current + 1)}>
          {failed ? 'Retry covering emails' : 'Refresh covering emails'}
        </button>
        <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" disabled={disabled || importing || !policyReady}
          onClick={() => setAdding((current) => !current)}>{adding ? 'Close email setup' : 'Add covering email'}</button>
      </div>
      {adding && (
        <div className="cur-forward-source cur-stack">
          <p className="cur-hint">Use a covering email suitable for both active and cancelled claims. Import its HTML, then preview it before saving the workflow.</p>
          <label className="cur-field">
            <span>Template ID</span>
            <input className="cur-input" aria-label={`New covering email template ID, step ${n}`} value={newId} maxLength={200}
              disabled={disabled || importing} onChange={(event) => setNewId(event.target.value)} />
          </label>
          {excluded.includes(newId.trim()) && <p className="cur-problem">This template ID is reserved for another process. Use a separate covering email.</p>}
          <div><button type="button" className="ui-btn ui-btn--secondary ui-btn--sm" disabled={disabled || importing || !canSetUp}
            onClick={() => setEditingId(newId.trim())}>Set up covering email</button></div>
          {editingId && <TemplateSourceEditor key={editingId} templateId={editingId} disabled={disabled}
            onBusyChange={setImporting} onImported={(source) => {
              setTemplates((current) => [...(current || []).filter((item) => item.template_id !== source.template_id), source]);
              onChange({ ...value, template_id: source.template_id, tokens: source.template_id === value.template_id ? value.tokens : {} });
            }} />}
        </div>
      )}
      <label className="cur-field">
        <span>Subject</span>
        <input className="cur-input" aria-label={`Subject for step ${n}`} value={value.subject} maxLength={300} disabled={disabled || importing}
          placeholder="Subject of the covering email" onChange={(event) => onChange({ ...value, subject: event.target.value.replace(/[\r\n]/g, ' ') })} />
      </label>
      {variables.length > 0 && (
        <fieldset className="cur-forward-variables" disabled={disabled || importing || !policyReady}>
          <legend>Personalise the covering email</legend>
          <p className="cur-hint">Choose verified claim details for each variable in the email and subject.</p>
          {variables.map((variable) => {
            const mapped = value.tokens[variable];
            const chosen = mapped ? choices.findIndex((choice) => tokenKey(choice.token) === tokenKey(mapped)) : -1;
            const saved = Boolean(mapped && chosen === -1);
            return (
              <label key={variable} className="cur-forward-variable">
                <code>{`{{${variable}}}`}</code>
                <select className="cur-input" aria-label={`Value for ${variable}, step ${n}`} value={saved ? 'saved' : chosen < 0 ? '' : String(chosen)}
                  onChange={(event) => mapVariable(variable, event.target.value)}>
                  <option value="">Choose a verified value</option>
                  {saved && <option value="saved" disabled>Saved mapping · unavailable</option>}
                  {choices.map((choice, position) => <option key={tokenKey(choice.token)} value={String(position)}>{choice.label}</option>)}
                </select>
              </label>
            );
          })}
        </fieldset>
      )}
      <p className="cur-hint">Choose wording that suits both claim states. Preview the subject and email before checking this step.</p>
    </div>
  );
};
